import './index.css';
import './utils.css';
import './i18n';

import { buildUnleashContext } from '@cutr/constants/feature-flags';
import { I18N_USAGE_FLAG } from '@cutr/constants/i18n-usage';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FlagProvider, UnleashClient } from '@unleash/proxy-client-react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { AuthState, useAuthStore } from '@/api/login';
import i18n, { i18nUsage } from '@/i18n';

import { router } from './App';
import NestingHud from './blocks/NestingHud/NestingHud';
import { FeatureProvider } from './FeatureProvider';
import { getCurrentFeatures, getTicker } from './theme';
import { ToastProvider } from './ToastProvider';
const ENVIRONMENT = import.meta.env.VITE_CUTR_ENV;
const RELEASE = import.meta.env.VITE_GIT_HASH;
const SENTRY_ACTIVE = ENVIRONMENT !== 'development';

window.analytics.load(import.meta.env.VITE_SEGMENT_WRITE_KEY);

Sentry.init({
  release: RELEASE,
  environment: ENVIRONMENT,
  dsn: SENTRY_ACTIVE
    ? 'https://9898aee78fc547889dff93f9ab853e22@o880313.ingest.sentry.io/4504050035851264'
    : undefined,
  integrations: [
    new Sentry.BrowserTracing({
      tracePropagationTargets: [
        'localhost',
        'http://local.cutr.com',
        'https://local.cutr.com',
        'https://apistaging.cutr.ai',
        'https://api.aws-staging.cutr.ai',
        'https://api.cutr.ai',
        'https://apistaging.cutr.com',
        'https://api.gcp-staging.cutr.com',
        'https://api.gcp-prod.cutr.com',
        'https://api.cutr.com',
      ],
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
  ],
  tracesSampler: (samplingContext) => {
    // inherit parent
    if (samplingContext.parentSampled !== undefined) {
      return samplingContext.parentSampled;
    }
    return 0.25;
  },
});

const unleashClient = new UnleashClient({
  url: 'https://unleash-edge.cutr.com/api/frontend',
  clientKey: import.meta.env.VITE_UNLEASH_CLIENT_KEY,
  refreshInterval: 15,
  appName: import.meta.env.VITE_CUTR_ENV,
  environment: import.meta.env.VITE_CUTR_ENV,
});

const syncUnleashContext = (state: AuthState) => {
  unleashClient.updateContext(
    buildUnleashContext({
      userEmail: state.email ?? undefined,
      sourceTicker: getTicker(),
      userAgent: navigator.userAgent,
      userLang: i18n.resolvedLanguage ?? undefined,
      clientNumber: state.clientNumber ?? undefined,
      agentId: state.agent?.id ?? undefined,
      devHostname: import.meta.env.VITE_DEV_HOSTNAME,
    })
  );
};
syncUnleashContext(useAuthStore.getState());
useAuthStore.subscribe(syncUnleashContext);

i18nUsage.start({
  url: `${import.meta.env.VITE_API_BASE_URL}/v2/i18n-usage`,
  isEnabled: () => unleashClient.isEnabled(I18N_USAGE_FLAG),
});

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <FlagProvider unleashClient={unleashClient}>
      <QueryClientProvider client={queryClient}>
        <FeatureProvider initialState={getCurrentFeatures()}>
          <ToastProvider />
          <RouterProvider router={router(queryClient)} />
        </FeatureProvider>
        <ReactQueryDevtools initialIsOpen={false} />
        {ENVIRONMENT !== 'production' && <NestingHud />}
      </QueryClientProvider>
    </FlagProvider>
  </React.StrictMode>
);

declare global {
  interface Window {
    analytics: {
      identify(
        userId?: string,
        traits?: { [key: string]: unknown },
        options?: { [key: string]: unknown },
        callback?: () => void
      ): void;
      track(
        event: string,
        properties?: { [key: string]: unknown },
        options?: { [key: string]: unknown },
        callback?: () => void
      ): void;
      page(
        category?: string,
        name?: string,
        properties?: { [key: string]: unknown },
        options?: { [key: string]: unknown },
        callback?: () => void
      ): void;
      load(
        category?: string,
        name?: string,
        properties?: { [key: string]: unknown },
        options?: { [key: string]: unknown },
        callback?: () => void
      ): void;
    };
  }
}
